import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HeaderComponent } from '../../components/header/header.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { EmptyStateComponent } from '../../components/empty-state/empty-state.component';
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { GlobalService } from '../../services/global.service';
import { DropdownService } from '../../services/dropdown.service';
import { AssessmentService } from '../../services/assessment.service';
import { EvaluatorListPageComponent } from '../evaluator-list-page/evaluator-list-page.component';
import { AdminDashboardService } from '../../services/admin-dashboard.service';

@Component({
  selector: 'app-dashboard-page',
  standalone: true,
  imports: [HeaderComponent, CommonModule, FormsModule, RouterLink, SelectInputComponent, EmptyStateComponent, SearchInputComponent, EvaluatorListPageComponent],
  templateUrl: './dashboard-page.component.html',
})
export class DashboardPageComponent {
  get is_super_admin(): boolean {
    return this.gs.user_role_data?.belongs_to === 'all';
  }

  // ─── Old dashboard (evaluator / head-evaluator) ───────────────────────
  params: any = {};
  evaluation_type: any = 'Evaluation';
  academic_year: any;
  subjects: any = [];

  // ─── Admin dashboard filters ──────────────────────────────────────────
  university_id: any = null;
  academic_year_id: any = null;
  exam_id: any = null;
  subject_id: any = null;
  overview_type: 'evaluator' | 'head_evaluator' = 'evaluator';
  evaluation_type_filter: string = 'Evaluation';
  evaluation_type_options = [
    { title: 'Evaluation', value: 'Evaluation' },
    { title: 'Re-Evaluation', value: 'Re-Evaluation' },
  ];

  // Admin dashboard data
  script_overview: any = null;
  faculty_overview: any[] = [];
  is_loading = false;

  // Cascading dropdown registry
  dropdownRegistry: { [key: number]: any } = {};

  // Cached filtered dropdown options (avoid method calls in template)
  filtered_academic_years: any[] = [];
  filtered_exams: any[] = [];
  filtered_subjects: any[] = [];

  constructor(
    private ar: ActivatedRoute,
    private route: Router,
    public gs: GlobalService,
    public ds: DropdownService,
    public ass: AssessmentService,
    public ads: AdminDashboardService,
  ) {}

  async ngOnInit() {
    if (this.is_super_admin) {
      await this.ds.get_dropdown_list(['university', 'academic-year', 'exam', 'subject', 'assessment']);
      this.update_filtered_options();
      this.load_dashboard();
    } else {
      await this.ds.get_dropdown_list(['academic-year']);

      const query_params = this.ar.snapshot.queryParams;
      if (query_params['academic_year_id']) {
        this.academic_year = Number(query_params['academic_year_id']);
      } else {
        const current_date = new Date();
        const current_year = current_date.getFullYear();
        const academic_year = this.ds.dropdown_list?.academic_list?.find((item: any) => {
          const from_date = new Date(item.from_date);
          const to_date = new Date(item.to_date);
          return current_date >= from_date && current_date <= to_date;
        });
        if (academic_year) {
          this.academic_year = academic_year.value;
        } else {
          this.academic_year = this.ds.dropdown_list?.academic_list?.find((item: any) =>
            item.title.includes(((current_year - 1) + '-' + current_year).toString())
          )?.value;
        }
      }
      this.applyFilter();
    }
  }

  // ─── Old dashboard methods ────────────────────────────────────────────
  applyFilter() {
    this.params.dashboard = true;
    this.params.academic_year_id = this.academic_year;
    this.params.evaluation_type = this.evaluation_type;
    this.route.navigate([], {
      relativeTo: this.ar,
      queryParams: { academic_year_id: this.academic_year },
      queryParamsHandling: 'merge'
    });
    this.get_list(this.params);
  }

  async get_list(filter?: any) {
    try {
      const res = await this.ass.get_list(filter);
      this.subjects = res.data;
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }

  applySearch(searchTerm: string) {
    this.params.search = searchTerm;
    this.get_list(this.params);
  }

  openPdf(dataUrl: string) {
    if (!dataUrl) return;
    fetch(dataUrl)
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.click();
      })
      .catch(err => console.error('Error opening PDF:', err));
  }

  // ─── Admin dashboard methods ──────────────────────────────────────────
  registerDropdown(event: { depended: number; ref: any }) {
    this.dropdownRegistry[event.depended] = event.ref;
  }

  onDropdownChange(event: { value: any; depended: number }) {
    for (const key of Object.keys(this.dropdownRegistry).map(Number)) {
      if (key > event.depended) this.dropdownRegistry[key]?.reset();
    }
    if (event.depended === 1) {
      this.university_id = event.value?.value ?? null;
      this.academic_year_id = null; this.exam_id = null; this.subject_id = null;
    } else if (event.depended === 2) {
      this.academic_year_id = event.value?.value ?? null;
      this.exam_id = null; this.subject_id = null;
    } else if (event.depended === 3) {
      this.exam_id = event.value?.value ?? null;
      this.subject_id = null;
    } else if (event.depended === 4) {
      this.subject_id = event.value?.value ?? null;
    }
    this.update_filtered_options();
    this.load_dashboard();
  }

  update_filtered_options() {
    const list = this.ds.dropdown_list;

    if (!this.university_id || !list?.assessment_list) {
      this.filtered_academic_years = list?.academic_list || [];
    } else {
      const ids = list.assessment_list
        .filter((a: any) => a.university_id == this.university_id)
        .map((a: any) => a.academic_year_id)
        .filter((id: any, i: number, arr: any[]) => arr.indexOf(id) === i);
      this.filtered_academic_years = list?.academic_list?.filter((a: any) => ids.includes(a.id)) || [];
    }

    if (!this.academic_year_id || !list?.assessment_list) {
      this.filtered_exams = list?.exam_list || [];
    } else {
      const ids = list.assessment_list
        .filter((a: any) => a.academic_year_id == this.academic_year_id && (!this.university_id || a.university_id == this.university_id))
        .map((a: any) => a.exam_id)
        .filter((id: any, i: number, arr: any[]) => arr.indexOf(id) === i);
      this.filtered_exams = list?.exam_list?.filter((e: any) => ids.includes(e.id)) || [];
    }

    if (!this.exam_id || !list?.assessment_list) {
      this.filtered_subjects = list?.subject_list || [];
    } else {
      const seen = new Set();
      this.filtered_subjects = list.assessment_list
        .filter((a: any) =>
          a.exam_id == this.exam_id &&
          (!this.university_id || a.university_id == this.university_id) &&
          (!this.academic_year_id || a.academic_year_id == this.academic_year_id)
        )
        .reduce((acc: any[], a: any) => {
          const sid = a.subject?.id || a.subject_id;
          if (sid && !seen.has(sid)) { seen.add(sid); acc.push({ title: a.subject?.name || 'none', value: sid }); }
          return acc;
        }, []);
    }
  }

  async load_dashboard() {
    this.is_loading = true;
    try {
      const params: any = { overview_type: this.overview_type };
      if (this.university_id) params.university_id = this.university_id;
      if (this.academic_year_id) params.academic_year_id = this.academic_year_id;
      if (this.exam_id) params.exam_id = this.exam_id;
      if (this.subject_id) params.subject_id = this.subject_id;

      const res: any = await this.ads.get_dashboard(params);
      this.script_overview = res?.data?.script_overview ?? null;
      this.faculty_overview = res?.data?.faculty_overview ?? [];
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message || 'Failed to load dashboard', 'Error', 'error');
    } finally {
      this.is_loading = false;
    }
  }

  set_overview_type(type: 'evaluator' | 'head_evaluator') {
    if (this.overview_type === type) return;
    this.overview_type = type;
    this.load_dashboard();
  }

  on_evaluation_filter_change() {
    this.load_dashboard();
  }

  get total_faculty_count(): number {
    if (!this.script_overview) return 0;
    return this.overview_type === 'head_evaluator'
      ? this.script_overview.total_head_evaluators
      : this.script_overview.total_evaluators;
  }

  format_overview_time(s: string): string {
    return s || '00:00:00';
  }

  format_time(s: string): string {
    return s || '00:00:00';
  }

  private donut_arc(cx: number, cy: number, R: number, r: number, startDeg: number, endDeg: number): string {
    const toRad = (d: number) => (d - 90) * Math.PI / 180;
    const sx = cx + R * Math.cos(toRad(startDeg));
    const sy = cy + R * Math.sin(toRad(startDeg));
    const ex = cx + R * Math.cos(toRad(endDeg));
    const ey = cy + R * Math.sin(toRad(endDeg));
    const isx = cx + r * Math.cos(toRad(startDeg));
    const isy = cy + r * Math.sin(toRad(startDeg));
    const iex = cx + r * Math.cos(toRad(endDeg));
    const iey = cy + r * Math.sin(toRad(endDeg));
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${sx} ${sy} A ${R} ${R} 0 ${large} 1 ${ex} ${ey} L ${iex} ${iey} A ${r} ${r} 0 ${large} 0 ${isx} ${isy} Z`;
  }

  get pie_chart_data(): any[] {
    if (!this.faculty_overview || this.faculty_overview.length === 0) return [];
    const total = this.faculty_overview.reduce((s: number, f: any) => s + (Number(f.evaluated_scripts) || 0), 0);
    if (total === 0) return [];
    const colors = ['#93C5FD', '#FB923C', '#FBBF24', '#86EFAC', '#C4B5FD', '#F9A8D4', '#6EE7B7', '#FCA5A5'];
    const cx = 110, cy = 110, R = 110, r = 37;
    let angle = 0;
    return this.faculty_overview.map((f: any, i: number) => {
      const pct = (Number(f.evaluated_scripts) || 0) / total;
      const start = angle;
      const end = angle + Math.min(pct * 360, 359.9999);
      angle = end;
      const mid = (start + end) / 2;
      const midRad = (mid - 90) * Math.PI / 180;
      const lR = (R + r) / 2;
      return {
        path: this.donut_arc(cx, cy, R, r, start, end),
        color: colors[i % colors.length],
        pct: (pct * 100).toFixed(1),
        lx: cx + lR * Math.cos(midRad),
        ly: cy + lR * Math.sin(midRad),
      };
    });
  }
}
